import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  effect,
  inject,
  Input,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { CurrentUserStore } from '@frontendlib/common/services/current-user.store';
import { BaseStitchListComponent, type StitchListItem } from '@modules/common/components/list/base-stitch-list.component';
import { ScrollToPosition } from '@modules/common/services/scroll-to-index.injection-token';
import { type StitchCardSelection, type StitchCardVariant } from '@modules/common/utils/stitch-card-variant';
import { type SelectableOpenEvent } from '@modules/drag-n-drop/types/selectable-open-event';
import { NoteComponent } from '@modules/elements/components/note/note.component';
import { LinkedInfoService } from '@modules/linked-info/services/linked-info.service';
import { NotesService } from '@modules/notes/services/notes.service';
import { NotesAppStateService } from '@modules/notes/services/state.service';
import { NotesTenantScopeService } from '@modules/notes/services/notes-tenant-scope.service';
import { Note } from '@modules/notes/types/note';
import { NotesFilters } from '@modules/notes/types/notes-filters';
import type { KnotSource } from '@sharedlib/common/types/knowledge/knot-source';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { BehaviorSubject, merge, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-notes-list',
  templateUrl: './notes-list.component.html',
  styleUrls: ['./notes-list.component.less'],
  providers: [{ provide: ScrollToPosition, useFactory: () => new BehaviorSubject<number>(null) }],
  standalone: false,
})
export class NotesListComponent
  extends BaseStitchListComponent<Note>
  implements OnInit, OnDestroy, OnChanges
{
  /** Show per-row notebook-location badges (search results, where results span notebooks). */
  public readonly showLocationBadge = input<boolean>(false);
  public readonly tenantItems = input<ReadonlyArray<{ readonly accountId?: string }>>();

  /** Show each row's hover quick actions; off for lists that only preview their items. */
  public readonly actionsButtonEnabled = input<boolean>(true);

  /** Preserve keyboard successor navigation unless the main item window opts out. */
  public readonly openOnRemoval = input(true);

  /** Container treatment forwarded to every card; the knowledge hub asks for `'card'`. */
  public readonly variant = input<StitchCardVariant>('row');

  /** Id of the item open in the dynamic panel, forwarded so that card draws itself as selected. */
  public readonly previewedItemId = input<string | null>(null);

  /** What each card draws as selected, forwarded; the knowledge panel asks for the list's own selection. */
  public readonly cardSelection = input<StitchCardSelection>('preview');

  /** The hub draws its related items as cards; every other caller keeps the app's flush row. */
  protected readonly isCard = computed(() => this.variant() === 'card');

  @Input()
  public override filters: Partial<NotesFilters> = {};
  @Input()
  public withTags = false;
  @Input()
  public withKnots = false;
  @Input()
  public knotsSource: KnotSource;
  @Input()
  public debug: 'score';

  @Output()
  public readonly clickNote = new EventEmitter<Note>();
  @Output()
  public readonly openNote = new EventEmitter<Note>();
  @Output()
  public readonly doubleClickNote = new EventEmitter<Note>();

  public items: StitchListItem<Note>[] = [];

  @ViewChildren(NoteComponent, { read: ElementRef<HTMLElement> })
  public override itemElements: QueryList<ElementRef<HTMLElement>>;

  private readonly notesService = inject(NotesService);
  private readonly linkedInfoService = inject(LinkedInfoService);
  private readonly notesStateService = inject(NotesAppStateService);
  private readonly notesTenantScopeService = inject(NotesTenantScopeService, { optional: true });
  private readonly currentUserStore = inject(CurrentUserStore);
  private accountSnapshot: string | null | undefined;
  private readonly accountResetEffect = effect(() => {
    const accountId = this.currentUserStore.accountId();
    if (this.accountSnapshot === undefined) {
      this.accountSnapshot = accountId;
      return;
    }
    if (accountId === this.accountSnapshot) return;
    this.accountSnapshot = accountId;
    this.resetItems();
  });

  protected override stitchSignalSpaceIds(): ReadonlyArray<string> {
    const contextId = this.spaceContextStore.contextId();
    const effectiveTenantId = this.notesTenantScopeService?.effectiveTenantId();
    const spaceIds = new Set<string>([contextId]);
    if (effectiveTenantId) {
      spaceIds.add(effectiveTenantId);
    }
    for (const { accountId } of this.tenantItems() ?? []) {
      if (accountId) {
        spaceIds.add(accountId);
      }
    }
    return [...spaceIds];
  }

  protected override getStitchType(): StitchType {
    return StitchType.NOTE;
  }

  public override ngOnInit() {
    merge(this.linkedInfoService.getRefresh(), this.notesService.getRefresh())
      .pipe(takeUntil(this.alive))
      .subscribe(() => {
        this.loadPage(this.page);
      });

    super.ngOnInit();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if ('filters' in changes) {
      this.resetItems();
    }
  }

  protected override getItems(offset: number, limit: number): Observable<{ items: Note[]; count: number }> {
    return this.notesService.search(
      {
        ...this.filters,
        archived: this.filters?.archived ?? false,
        deleted: this.filters?.deleted ?? false,
        limit,
        offset,
      },
      {
        withTags: this.withTags,
        withKnots: this.withKnots,
        withConnections: false,
        knotsSource: this.knotsSource,
        tenantItems: this.tenantItems(),
      },
    );
  }

  public handleDoubleClickItem(note: Note) {
    this.doubleClickNote.emit(note);
  }

  public handleOpenSelected(event: SelectableOpenEvent<Note>): void {
    if (event.source === 'removal' && !this.openOnRemoval()) return;
    this.clickNote.emit(event.item.data);
  }
}
